import * as React from "react";
import CGReactPage from "./cgreact-page";
import CGReactApplication from "./cgreact-application";
import CGReactLayout from "./cgreact-layout";
import CGReactModal from "./cgreact-modal";
import {CompProp, CompState} from "../types/cgreact-type-component";
import {copyObject, mergeObject, supplyObject} from "./cgreact-util";

/** CGReactComponent类定义-抽象一个组件（包含对话框）*/
export default abstract class CGReactComponent<STATE extends CompState = CompState, PROP extends CompProp = CompProp> extends React.Component<PROP, STATE>
{
    /** CGReact组件类型*/
    public static contextType = CGReactApplication.ApplicationContext; //自动注入this.context=ApplicationContext
    public static ParentType = CGReactPage.ParentPageContext;//自动注入this.parent=ParentPageContext
    public static CGReactModuleType = "CGReactComponent"; //从Class或者JSX中可以获得和判断组件的类型
    public static RecentClassInstance: any; //保存本类或者子类的最新的一个实例
    /** 全局APP上下文*/
    public context: CGReactApplication; // 全局应用上下文
    public parent: CGReactPage;//父页面的引用，通过parent获得父控件的引用
    public layout: CGReactLayout; // 元素嵌套的父布局框架
    public modal: CGReactModal; //页面集成的modal类引用
    public JSSStyle: { [key in string]: JSS }; //通过affectStyle注解生效的所有JSS样式
    public CSSStyle: { [key in string]: string }; //通过affectStyle注解生效的所有CSS样式
    public SCSSStyle: { [key in string]: string }; //通过affectStyle注解生效的所有SCSS样式
    public style: JSSKeyMap<StyleKey>; //页面样式的CSSModule类
    /**
     * state在CGReact中被设计为一个ES7的动态代理对象（ES7高级语法支持）
     * 它能够侦听普通的数据对象赋值的操作，比如：this.state.xxx = xxx
     * 我们可以捕捉到这个赋值事件，完成页面的自动刷新
     * 参考：injectRefreshStateToObject()方法
     * this.state的重新赋值，虽然不会生效（每次render都重新生成state_original的代理对象）
     * 但是会让用户觉得奇怪，复写state不生效也没提示，因此给他readonly属性，这样用户在尝试直接覆盖state时会报错
     */
    public readonly state: STATE;
    /**
     * CGReact设计在组件Component中：state_original=props.data
     * 根据React的状态提升的思想，我们会让：
     * 1.用户通过data传递过来的是父页面中state_original的一个片段
     * 2.在Component对于state_original的修改，都将同步到父页面的state数据
     * 为了让state_original=props.data始终引用父页面的数据片段，不允许将state_original重新赋值
     * 这个数据结构是真正的状态提升state片段，是state的被代理对象，希望对用户保持透明，因此用private修饰
     */
    public state_original: STATE;
    /** 页面标识位*/
    private StateFlag = true;//为了避免异步代码空间中多次赋值导致的页面重复刷新多次，定义一个变量只让第一次生效:参考：this.refresh()
    public MountOK = false; //Component完成数据初始化，并成功二次渲染后才会为true
    /** 构造函数，prop无需传入任何参数，用state来控制*/
    constructor(props: PROP)
    {
        super(props);
        // @ts-ignore 此类和继承此类的所有子类，保存最后一个类实例
        this.constructor.RecentClassInstance = this;
    }

    /**
     * 初始化组件数据
     * 在constructor之后组件渲染时，onComponentStateReady,renderView和onComponentDidMount之前调用
     * 使用时需要注意：
     * 1.initState中不能访问this.state
     * 对于普通Component，此时state还没有初始化，访问返回null
     * 对于对话框MocalComponent，state返回的是之前构造的已经过期的数据
     * 2.initState中可以正常访问this.prop，将获得最新传入的数据
     */
    protected async initState(init: STATE): Promise<STATE>
    {
        //按照逻辑init是display传入和prop传入的合并
        return init;
    }

    /** 初始化用户界面*/
    protected abstract renderView(): JSX;

    /** 数据初始化完成之前的加载中页面*/
    protected renderLoadingView(): JSX
    {
        return undefined;
    }

    /**原生React方法，当控件在界面上加载（首次渲染）时调用,此方法调用发生在render()之后*/
    componentDidMount(): void
    {
        // 自动绑定全局插槽
        typeof this["onWindowResize"] == "function" && (this.context.WindowResizeSignal.bind(this as any));
        typeof this["onKeyDown"] == "function" && (this.context.KeyboardPressSignal.bind(this as any));
        // 加载页面数据
        !this.modal && (this.overrideState());
    }

    /* 原生React方法，在控件更新渲染后调用*/
    componentDidUpdate(prevProps: Readonly<PROP>, prevState: Readonly<STATE>, snapshot?: any): void
    {
        // 根据state状态来判断是否第一次mount
        if (this.state_original)
        {
            if (this.MountOK == false)
            {
                // 生效引入外部样式
                this.context.affectSpecificStyle(this.JSSStyle, this.CSSStyle, this.SCSSStyle);
                // 触发生命周期回调
                this.onCGReactCompMount();
                this.onComponentDidMount();
                this.MountOK = true;
            }
            this.onCGReactCompUpdate();
            this.onComponentDidUpdate();
        }
    }

    /** 原生方法，在组件卸载时调用*/
    componentWillUnmount(): void
    {
        // 回调用户接口
        this.onComponentUnmount();
        // 自动解除全局插槽
        typeof this["onWindowResize"] == "function" && (this.context.WindowResizeSignal.unbind(this as any));
        typeof this["onKeyDown"] == "function" && (this.context.KeyboardPressSignal.unbind(this as any));
    }

    /** 继承setState 防止用户调用*/
    setState(state): void
    {
        console.warn("您的调用无效，CGReact中直接数据赋值驱动，无需调用setState");
    }

    /**
     * 处理页面State的初始化
     * 1.对于一般的Component组件，initState必须放到componentDidMont中
     * 因为用户很可能在initState的时候使用父控件的state数据
     * 而只有componentMount上去的时候，React的Context框架才能够获得其context值
     * 2.对于模态框的ModalComponent组件，initState放在onDisplay中比较合适
     */
    private overrideState(state_display?: FreeNecessary<STATE>): void
    {
        /**
         * 构造数据初始化前的state的值
         * 经display传入的state和prop，与initState构造的值进行合并
         * 优先级按照：
         * initState() > display > prop
         */
        let init: STATE = this.props.data ? this.props.data as STATE : {} as STATE; // 如果有prop直接使用优先级最低的prop
        /**
         * 优先级居中的display值覆盖，这一步有2个效果：
         * 1.display数据覆盖init中的原有数据，实现display优先级高于prop
         * 2.如果prop是传入了的（状态提升）那么prop中的值也会被display覆盖
         */
        if (state_display)
        {
            // 拷贝display值覆盖到init中
            mergeObject(state_display, init);
        }
        // 初始化数据函数
        this.initState(init).then((data) =>
        {
            /**
             * 如果用户初始化了initState，那么这里很可能是一个新的对象
             * 直接覆盖init 实现initState()构造 的最大优先级
             */
            if (typeof data == "object")
            {
                mergeObject(data, init);
            }
            // 保存state构造结果
            this.state_original = init;
            this.makeState(this.state_original);
            // 用户回调
            this.onComponentStateReady();
            /**
             * 重新渲染页面内容，
             * 因为可能修改了数据容器（父没有传递数据容器的情况）
             * 因此需要将新的容器同步赋值到父页面因此sync参数为true
             */
            this.refresh(true);
        });
    }

    /** 改变state的方法，避免用户调用*/
    private makeState(newState): void
    {
        //@ts-ignore
        this.state = newState;
    }

    /** 递归的使用ES7的动态代理Proxy技术，为state中的所有object注入监听函数，做到数据赋值的自动刷新*/
    private makeStateProxy<T>(obj: T): T
    {
        // 判断是否为object或者array
        if (typeof obj != "object" || React.isValidElement(obj))
        {
            return obj;
        }
        let proxy: any = obj instanceof Array ? [] : {};
        // 继续递归拷贝
        for (let key in obj)
        {
            proxy[key] = this.makeStateProxy(obj[key]);
        }
        // @ts-ignore 注入监听方法
        proxy = new Proxy(proxy, {
            set: (target, key, value, receiver): boolean =>
            {
                // console.log("." + key.toString() + "=" + value);
                /**
                 * 为了防止用户使用state自我拷贝导致state_original变为代理的问题
                 * 比如：this.state.obj1=this.state.obj2  (此时obj2是一个Proxy对象）所以
                 * value会拿到代理对象值，从而把obj[key]（其实就是state_original)变成代理
                 * 我们需要把代理对象还原为普通对象
                 */
                obj[key] = copyObject(value);
                /**
                 * 为了防止用户将state中的对象从undefine改为object并后续赋值导致的报错
                 * 比如： this.state.obj1={}  (obj1原来是undefine，当然也没有代理对象）
                 * 然后 this.state.xx=111 会报错，因为代理对象的xx仍然是undefine
                 * 因此需要让代理同步这个新注入的对象
                 */
                target[key] = this.makeStateProxy(obj[key]);
                // 代理自动刷新页面
                this.refresh(true);
                return true;
            }
        });
        return proxy;
    }

    /** 绑定react原生渲染界面函数*/
    render(): React.ReactElement
    {
        /**
         * 这样做主要有2个原因：
         * 1.setState的方式是克隆，因此setState方法会将原来的state=props.data
         * 变成props.data的克隆，这样state就不在是props（也就是父页面数据）的一个片段了
         * 因此这里需要还原一下，否则当父改变数据是，子组件中使用this.state初始化的内容都不会刷新
         * 2.在用户initView中不宜使用代理对象，如果将Proxy代理传给子组件及有可能导致报错
         * 因此这里将其还原为状态提升的state_original(它会是父的一个片段，并且是普通数据）
         * 这样既不会报错，也保障了状态提升能一直传递给子组件
         */
        this.makeState(this.state_original);
        // 调用页面渲染调用连获取页面
        let content = container.call(this);
        //页面渲染完成，继续将this.state变为代理对象，监听用户赋值操作
        this.makeState(this.makeStateProxy(this.state_original));
        return content;

        function container(): JSX
        {
            return <React.Fragment>
                {parent.call(this)}
            </React.Fragment>;
        }

        function parent(parent): JSX
        {
            return <CGReactComponent.ParentType.Consumer>
                {(parent) =>
                {
                    this.parent = parent;
                    return modal.call(this);
                }}
            </CGReactComponent.ParentType.Consumer>
        }

        function modal(): JSX
        {
            // 如果配置了modal参数，则渲染模态框
            let needModal = this.props.modal;
            return needModal ? React.createElement(CGReactModal, {
                component: this,
                modal: this.props.modal
            }, layout.call(this)) : layout.call(this);
        }

        function layout(): JSX
        {
            // 如果组件配置参数中出现了layoutXXX那么，需要渲染布局
            let needLayout = Object.keys(this.props).some(cur => cur.search(/layout.*/) > -1);
            needLayout = needLayout || Object.keys(this.props).indexOf("direction") > -1; //配置了direction
            needLayout = needLayout || Object.keys(this.props).indexOf("gravity") > -1; //配置了gravity
            needLayout = needLayout || Object.keys(this.props).indexOf("align") > -1; //配置了align
            return needLayout ? React.createElement(CGReactLayout, mergeObject(this.props, {
                component: this//传入Component的引用作为Content保存在layout中
            }), view.call(this)) : view.call(this);
        }

        function view(): JSX
        {
            if (!this.state_original)
            {
                let loadingView = this.renderLoadingView();
                loadingView = loadingView ? loadingView : this.context.renderComponentDefaultLoadingView();
                return loadingView;
            }
            /**
             * 对于Component组件和Page不同的是
             * 当父页面更新了整个子组件的数据，通过prop传入了一个新的数据片段
             * 原有的state_original就不是父组件的片段了，所以要注意保持同步
             * 通过prop传入的数据可能是不健全的（在状态提升中，只传入了一个容器，也有可能赋值为不全的状态）
             */
            if (this.props.data && this.props.data != this.state_original)
            {
                this.state_original = supplyObject(this.state_original, this.props.data) as STATE;
            }
            /** 渲染组件*/
            return this.renderView();
        }
    }

    /**
     * 无条件刷新页面
     * 有的时候可能出现了用户用自己定义的数据结构渲染界面的情况
     * 此时只是用户数据结构变化，state没有变动，因此不会触发重新渲染
     * 此时用户通过调用此函数触发无条件渲染
     * @param sync 是否强制刷新父页面和组件
     */
    public refresh(sync?: boolean): void
    {
        /**
         * React特性:
         * 1.同步方法中调用多次setState只会触发1次render
         * 2.异步方法中调用多次setState会触发多次render
         * 为了避免在异步代码空间中用户多次赋值导致的多次渲染，只让第一次渲染有效
         */
        let parent: any = this.parent;
        if (this.StateFlag && (!parent || (parent && parent.StateFlag)))
        {
            this.StateFlag = false;
            setTimeout(() =>
            {
                /**
                 * 当状态提升时，本类的state_original是父页面的一个state_original片段
                 * 当本类数据变动涉及到对象的重新赋值时，比如this.state.xxxx={}
                 * 新对象会被代理写入父页面的state_original中，但是父页面或者父组件（组件套用组件的情况）
                 * 的代理并没有更新，它还指向旧的对象或者引用旧对象的值，导致错误
                 * 因此需要将组件的所有父组件（可能嵌套了多层）包括Page根页面的代理对象都刷新
                 * 直接调用this.context.refresh()将导致父页面和所有嵌套父组件的render()
                 * render()函数中会重新将所有组件的代理对象全部刷新
                 */
                sync && parent && parent.makeState(parent.makeStateProxy(parent.state_original));
                sync && parent && parent.refresh();
                /**
                 * React的setState({})方法无条件刷新页面且不会更改任何state数据
                 * 另外CGReact中原生数据state总是{}
                 * 在render()时，渲染页面前被赋值为state_original,渲染页面后赋值为state_original的代理对象
                 */
                super.setState({});
                // 重开刷新响应
                this.StateFlag = true;
            }, 100);
        }
    }

    /**
     * 重新加载当前组件，会清除state重新调用initState赋值
     * @param state 重新加载时可以重载界面数据
     * @param saveProp 是否保留状态提升的prop数据，默认不保留
     * 此数据设置的优先级最高
     */
    public reload(state?: FreeNecessary<STATE>): void
    {
        // 重置加载标识
        this.MountOK = false;
        // state数据需要重新更新
        this.state_original = undefined;
        // 重新加载数据
        this.overrideState(state);
    }

    /**
     * 此方法针对ModalComponent生效，普通Component调用无效果
     * 展示模态对话框框
     */
    public display(state?: FreeNecessary<STATE> | "NoReload"): void
    {
        if (!this.props.modal)
        {
            // 非对话框模式直接返回
            return;
        }
        // 初始化state_display
        let state_display = typeof state == "object" ? state : null;
        // 设置对话框显示
        this.modal.visible = true;
        // 修改对话框显示参数，重新渲染页面
        if (state == "NoReload")
        {
            // 显示之前对话框，如果为初始化就初始化
            this.state_original ? this.refresh() : this.overrideState(state_display);
        } else
        {
            // 重载当前组件数据
            this.reload(state_display);
        }
    }

    /**
     * 此方法针对ModalComponent生效，普通Component调用无效果
     * 模态对话框消失
     */
    public dismiss(): void
    {
        if (!this.props.modal)
        {
            return;
        }
        this.modal.dismiss();
    }

    /** 保留给框架内部条用的回调接口，不希望用户继承*/
    public onCGReactCompMount(): void
    {
    }

    /** 保留给框架内部条用的回调接口，不希望用户继承*/
    public onCGReactCompUpdate(): void
    {
    }

    /** 保留给框架内部条用的回调接口，不希望用户继承*/
    public onCGReactLayoutResize(): void
    {
    }

    /**
     * 当state数据通过initState异步方法成功准备并返回的时候调用此方法
     * 对于普通组件此方法只在渲染时调用1次
     * 对于模态组件此方法会在每次渲染时调用
     */
    protected onComponentStateReady(): void
    {
    }

    /**
     * 当界面数据完成初始化时调用此方法，在页面加载时调用一次，重新渲染不会再次调用
     * 注意这里和原生不太一样，由于initData方法是异步的，因此这里的调用要比原生的componentDidMount晚一些
     * 既异步数据初始化完成后，才调用这个方法，此时保障了this.state已经获得数据，并且调用一定在原生mount方法之后
     * 此时：state为代理对象
     */
    protected onComponentDidMount(): void
    {
    }

    /**
     * 当界面重新渲染时调用此方法，每次render之后都会调用
     * 此时：state为代理对象
     */
    protected onComponentDidUpdate(): void
    {
    }

    /** 组件卸载或者对话框关闭不显示时回调此函数*/
    protected onComponentUnmount(): void
    {
    }

    /** 集成模态框关闭的时候回调此函数*/
    protected onModalComponentCancel(): void
    {
    }

    /** 集成模态框确认按钮点击的时候回调*/
    protected onModalComponentConfirm(): void
    {
    }
}
